import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  FeaturedScheduleDay,
  IAppointments,
  getFeaturedScheduleDays,
  useGetAvailableTimesMutation
} from '../../../../services/availableTimes.service';
import {
  filterByDay,
  getAllAvailableDaysOfTheMonth,
  getMonth,
  getNextMonth,
  getShortedAvailableDays,
  getYear,
  groupScheduleAvailableTimes,
  GroupTimes
} from '../../../../lib/dayjs';
import { RootState } from '../../../../store';

export interface IAppointmentsAvailable {
  calendarId: string;
  doctorId?: string;
  type?: string;
}

export const useAppointmentsAvailable = () => {
  const {
    calendar: { calendarsAvailables, doctorId, journey, withTheSameDoctor, typeOfAppointment, isReSchedule, schedule }
  } = useSelector((state: RootState) => state);

  const [appointments, setAppointments] = useState<IAppointments>({ availableList: [], availableListAddress: [] });
  const [currentMonth, setCurrentMonth] = useState<string>(getMonth() || '');
  const [currentYear, setCurrentYear] = useState<string>(getYear() || '');
  const [getAvailableTimes, { data, isLoading, status }] = useGetAvailableTimesMutation();
  const retryNextMonth = useRef(true);

  useEffect(() => {
    if (calendarsAvailables.length === 0) return;
    getAvailableTimes({
      calendarsAvailables,
      journey,
      doctorId: doctorId ? doctorId : null,
      withTheSameDoctor: withTheSameDoctor ? withTheSameDoctor : null,
      typeOfAppointment: typeOfAppointment ? typeOfAppointment : null,
      month: currentMonth,
      year: currentYear,
      isReschedule: isReSchedule ? isReSchedule : null,
      schedule: schedule ? schedule : null
    });
  }, [calendarsAvailables, currentMonth, currentYear]);

  useEffect(() => {
    const nextMonth = getNextMonth();

    if (data !== undefined) {
      setAppointments({
        availableList: [...new Set([...appointments.availableList, ...data.availableList])],
        availableListAddress:
          appointments.availableListAddress.length === 0
            ? data.availableListAddress
            : appointments.availableListAddress.reduce((acc: { [key: string]: string[] }[], item) => {
                const calendarId = Object.keys(item)[0];
                const availableList = item[calendarId];
                const newAvailableList = data.availableListAddress.find(item => Object.keys(item)[0] === calendarId);
                if (newAvailableList) {
                  acc.push({ [calendarId]: [...new Set([...availableList, ...newAvailableList[calendarId]])] });
                }
                return acc;
              }, [])
      });
      if (data.availableList.length < 2 && getMonth() === currentMonth && retryNextMonth.current === true) {
        retryNextMonth.current = false;
        setCurrentMonth(nextMonth);
      }
    }
  }, [data, currentMonth, currentYear]);

  return { appointments, setCurrentMonth, setCurrentYear, setAppointments, isLoading, status };
};

export const useShortedAvailableDays = (appointmentsAvailable: IAppointments) => {
  const [shortedAvailableDays, setShortedAvailableDays] = useState<string[]>([]);
  useEffect(() => {
    setShortedAvailableDays(getShortedAvailableDays(appointmentsAvailable));
  }, [appointmentsAvailable?.availableList]);

  return [shortedAvailableDays] as const;
};

export const useAllAvailableDaysOfTheMonth = (appointmentsAvailable: IAppointments) => {
  const [allAvailableDaysOfTheMonth, setAllAvailableDaysOfTheMonth] = useState<string[]>([]);
  useEffect(() => {
    setAllAvailableDaysOfTheMonth(getAllAvailableDaysOfTheMonth(appointmentsAvailable));
  }, [appointmentsAvailable?.availableList]);

  return [allAvailableDaysOfTheMonth] as const;
};

export const useScheduleAvailableTimeGroupedByPeriod = (appointments: IAppointments, day: string) => {
  const [scheduleAvailableTimeGroupedByPeriod, setScheduleAvailableTimeGroupedByPeriod] = useState<GroupTimes>({
    morning: { availableList: [], availableListAddress: [] },
    afternoon: { availableList: [], availableListAddress: [] },
    evening: { availableList: [], availableListAddress: [] }
  });

  useEffect(() => {
    setScheduleAvailableTimeGroupedByPeriod(groupScheduleAvailableTimes(filterByDay(day, appointments)));
  }, [appointments, day]);

  return [scheduleAvailableTimeGroupedByPeriod];
};

export const useGetFeaturedSchedules = (appointments: IAppointments, enables = false) => {
  const [featuredScheduleDays, setFeaturedScheduleDays] = useState<FeaturedScheduleDay[]>([]);
  useEffect(() => {
    if (!enables) {
      setFeaturedScheduleDays([]);
      return;
    }
    const featured = getFeaturedScheduleDays(appointments.availableListAddress);
    setFeaturedScheduleDays(featured);
  }, [enables, appointments.availableListAddress]);

  return featuredScheduleDays;
};
